import type { ApiError } from "@circle-react/config/CustomErrors";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { getLessonKey } from "@circle-react/hooks/courses/useCoursesApi";
import type { CourseLessonResponse } from "@circle-react/lib/queries/courses.types";
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import I18n from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import {
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import { useSpaceRouteParams } from "@/react/hooks/useSpaceRouteParams";

export const useLessons = ({
  forEdition = false,
}: {
  forEdition?: boolean;
}) => {
  const { lessonId, sectionId } = useSpaceRouteParams();
  const queryClient = useQueryClient();
  const { data: space } = useCurrentSpaceContext();
  const toast = useToast();

  const queryKey = ["lesson", lessonId, space?.id, sectionId];

  const {
    data: currentLesson,
    error: lessonError,
    isInitialLoading,
  } = useQuery<Partial<CourseLessonResponse>, ApiError>({
    queryKey,
    queryFn: () =>
      reactQueryGet(
        internalApi.courses.getLesson({
          courseId: space?.id,
          lessonId,
          sectionId,
          params: { for_edition: forEdition },
        }),
      ),
    enabled: !!lessonId && !!space && !!sectionId,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  useEffect(() => {
    if (lessonError?.status === 401) {
      window.location.href = "/";
    }
  }, [lessonError]);

  const { mutate: updateLesson, isPending: isUpdatingLesson } = useMutation<
    unknown,
    ApiError,
    Record<string, unknown>
  >({
    mutationFn: data =>
      reactQueryPut(
        internalApi.courses.updateLesson({
          courseId: space?.id,
          lessonId,
          sectionId,
        }),
        { course_lesson: data },
      ),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey, exact: true });
      queryClient.removeQueries({
        queryKey: getLessonKey(space?.id, lessonId),
      });
      toast.success(I18n.t("courses.edit.lesson.changes_saved"));
    },
    onError: e => {
      toast.error(
        typeof e.errorDetails === "string" ? e.errorDetails : e.message,
      );
    },
  });

  const { mutateAsync: duplicateLesson } = useMutation<
    { id: number },
    ApiError,
    {
      spaceId: number | string;
      sectionId: number | string;
      lessonId: number | string;
    }
  >({
    mutationFn: ({ spaceId, sectionId, lessonId }) =>
      reactQueryPost(
        internalApi.courses.duplicateLesson({
          courseId: spaceId,
          sectionId: sectionId,
          lessonId: lessonId,
        }),
      ),
    onError: () => toast.error(I18n.t("courses.edit.lesson.duplication_error")),
  });

  const { mutate: updateFilesOrder } = useMutation({
    mutationFn: ({ filesOrder }: { filesOrder: string[] }) =>
      reactQueryPut(
        internalApi.courses.updateLesson({
          courseId: space?.id,
          lessonId,
          sectionId,
        }),
        {
          course_lesson: {
            files_order: filesOrder,
          },
        },
      ),
  });

  return {
    currentLesson,
    isLoading: isInitialLoading,
    updateLesson,
    isUpdatingLesson,
    updateFilesOrder,
    duplicateLesson,
  };
};
